import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import apiAgencies from "../features/dataManagement/services/agency"

const AGENCY_TYPE_CACHE_KEY = 'agencyTypeCache'

const pluralize = (word, count) => {
  if (word.endsWith('y')) {
    return count > 1 ? `${word.slice(0, -1)}ies` : word
  }
  return count > 1 ? `${word}s` : word
}

const mapAgencyType = (type) => {
  switch (type) {
    case 'Agency & Clients':
      return {
        agencyType: 'agency',
        organizationType: 'client',
        agAbbr: 'AGY',
        orgAbbr: 'CLT',
      }
    case 'Holding & Subsidiaries':
      return {
        agencyType: 'holding',
        organizationType: 'subsidiary',
        agAbbr: 'HOLD',
        orgAbbr: 'SUB',
      }
    case 'Main Operator & Companies':
      return {
        agencyType: 'operator',
        organizationType: 'company',
        agAbbr: 'OP',
        orgAbbr: 'CO',
      }
    case 'Team & Depratments':
      return {
        agencyType: 'team',
        organizationType: 'department',
        agAbbr: 'TM',
        orgAbbr: 'DEP'
      }
    default:
      return {
        agencyType: 'agency',
        organizationType: 'organization',
        agAbbr: 'AG',
        orgAbbr: 'ORG',
      }
  }
}

const capitalizeString = (string) =>
  string.charAt(0).toUpperCase() + string.slice(1)

const useAgencyType = (agencyId, capitalize = false, plural = false) => {
  const { t, i18n } = useTranslation()
  const [agencyType, setAgencyType] = useState('')
  const [organizationType, setOrganizationType] = useState('')
  const [agAbbr, setAgAbbr] = useState('')
  const [orgAbbr, setOrgAbbr] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!agencyId) {
      setLoading(false)
      return
    }

    const fetchAgencyData = async () => {
      try {
        let type
        const cache =
          JSON.parse(localStorage.getItem(AGENCY_TYPE_CACHE_KEY)) || {}

        if (cache[agencyId]) {
          type = cache[agencyId]
        } else {
          const response = await apiAgencies.getSingle(agencyId)
          type = response.type
          const newCache = { ...cache, [agencyId]: type }
          localStorage.setItem(AGENCY_TYPE_CACHE_KEY, JSON.stringify(newCache))
        }

        const {
          agencyType: fetchedAgencyType,
          organizationType: fetchedOrganizationType,
          agAbbr: fetchedAgAbbr,
          orgAbbr: fetchedOrgAbbr,
        } = mapAgencyType(type)

        const currentLang = i18n.language
        const translate = (key) => t(`agencyTypes.${key}`)

        const translatedAgencyType = plural
          ? translate(pluralize(fetchedAgencyType, 2))
          : translate(fetchedAgencyType)

        const translatedOrganizationType = plural
          ? translate(pluralize(fetchedOrganizationType, 2))
          : translate(fetchedOrganizationType)

        if (capitalize && currentLang !== 'ar') {
          setAgencyType(capitalizeString(translatedAgencyType))
          setOrganizationType(capitalizeString(translatedOrganizationType))
        } else {
          setAgencyType(translatedAgencyType)
          setOrganizationType(translatedOrganizationType)
        }

        setAgAbbr(fetchedAgAbbr)
        setOrgAbbr(fetchedOrgAbbr)
      } catch (err) {
        setError('Failed to fetch agency data')
      } finally {
        setLoading(false)
      }
    }

    fetchAgencyData()
  }, [agencyId, capitalize, t, i18n.language, plural])

  return {
    agencyType,
    organizationType,
    agAbbr,
    orgAbbr,
    loading,
    error,
  }
}

export default useAgencyType
